import { HttpErrorResponse, HttpRequest } from "@angular/common/http";
import { SubscriptionPaymentRequiredReason } from "../../modals/subscription/subscription.interface";
import { SubscriptionInfo } from "../../services/api";
import { getHttpErrorMessage } from "../../utils/http-error-message/http-error-message";

const TRANSACTION_PATTERNS: RegExp[] = [
  /\btransactions?\b/,
  /transactions?[_-]?limit/,
  /\borders?\b/,
  /orders?[_-]?limit/,
  /free[_-]?orders?/,
  /usage[_-]?orders?/,
];

const PASS_PATTERNS: RegExp[] = [
  /\bloyalty cards?\b/,
  /\bwallets?\b/,
  /\bpasses?\b/,
  /free[_-]?passes?/,
  /installed[_-]?passes?/,
  /passes?[_-]?limit/,
  /\/send-pass\b/,
  /\/apple\/pass\b/,
  /\/google\/pass\b/,
];

export const resolvePaymentRequiredReason = (
  error: HttpErrorResponse,
  request: HttpRequest<unknown>,
  subscriptionInfo: SubscriptionInfo,
): SubscriptionPaymentRequiredReason => {
  const responseContext = [
    getHttpErrorMessage(error),
    collectStringContext(error.error),
  ].join(" ").toLowerCase();
  const requestContext = request.urlWithParams.toLowerCase();
  const responseReason = resolveReasonFromContext(responseContext);
  const requestReason = resolveReasonFromContext(requestContext);

  if (responseReason !== "unknown") return responseReason;
  if (requestReason !== "unknown") return requestReason;
  if (hasReachedPassLimit(subscriptionInfo)) return "passes";
  if (hasReachedTransactionLimit(subscriptionInfo)) return "transactions";

  return "unknown";
};

const resolveReasonFromContext = (context: string): SubscriptionPaymentRequiredReason => {
  const matchesTransactions = matchesAnyPattern(context, TRANSACTION_PATTERNS);
  const matchesPasses = matchesAnyPattern(context, PASS_PATTERNS);

  if (matchesTransactions && !matchesPasses) return "transactions";
  if (matchesPasses && !matchesTransactions) return "passes";
  return "unknown";
};

const matchesAnyPattern = (context: string, patterns: RegExp[]): boolean => patterns.some(pattern => pattern.test(context));

const hasReachedPassLimit = (subscriptionInfo: SubscriptionInfo): boolean =>
  subscriptionInfo.freePasses > 0 && subscriptionInfo.installedPasses >= subscriptionInfo.freePasses;

const hasReachedTransactionLimit = (subscriptionInfo: SubscriptionInfo): boolean =>
  subscriptionInfo.freeTransactions > 0 && subscriptionInfo.transactions >= subscriptionInfo.freeTransactions;

const collectStringContext = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(item => collectStringContext(item)).join(" ");
  if (!isRecord(value)) return "";

  return Object.entries(value)
    .map(([key, item]) => `${key} ${collectStringContext(item)}`)
    .join(" ");
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;
